/**
 * Redacted error logging.
 *
 * Never `console.*` a full error object: axios errors carry `response.data`,
 * request bodies, and `Authorization` headers, any of which can leak tokens or
 * credentials (see .claude/rules/security.md). This extracts only the safe,
 * useful shape and is the single place we log errors, so production stripping
 * of `console.*` can be wired here later.
 */

export interface RedactedError {
  context: string;
  message?: string;
  code?: string;
  status?: number;
  method?: string;
  /** Request path only — the query string is dropped (it may carry tokens). */
  url?: string;
}

const stripQuery = (url: string): string => {
  const i = url.indexOf('?');
  return i === -1 ? url : url.slice(0, i);
};

export const redactError = (context: string, error: unknown): RedactedError => {
  const redacted: RedactedError = { context };

  if (typeof error === 'string') {
    redacted.message = error;
    return redacted;
  }
  if (!error || typeof error !== 'object') {
    return redacted;
  }

  const e = error as {
    message?: unknown;
    code?: unknown;
    response?: { status?: unknown };
    config?: { url?: unknown; method?: unknown };
  };

  if (typeof e.message === 'string') redacted.message = e.message;
  if (typeof e.code === 'string') redacted.code = e.code;

  const rawStatus = e.response?.status;
  const status =
    typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;
  if (typeof status === 'number' && !Number.isNaN(status)) {
    redacted.status = status;
  }

  if (typeof e.config?.method === 'string') redacted.method = e.config.method;
  if (typeof e.config?.url === 'string') redacted.url = stripQuery(e.config.url);

  return redacted;
};

/** Log an error as a redacted, token-safe shape. */
export const logError = (context: string, error: unknown): void => {
  // No console output in production builds (see .claude/rules/security.md):
  // the redacted shape is for local/staging debugging and there is no prod sink.
  if (import.meta.env.PROD) return;
  console.error('[error]', redactError(context, error));
};
